// Playbook content + tool-link config. Adding a lesson = adding an entry.
// Adding a new tool = update TOOL_LINKS below in one place.

// Reusable tool-link registry. When a new Parlay Assistant tool is built,
// point its key here at the new route; every lesson that references the key
// will pick it up automatically. Falls back to homepage when unset.
export const TOOL_LINKS = {
  "odds-converter": { label: "Try the Odds Converter", href: "/#odds-converter" },
  "parlay-calculator": { label: "Try the Parlay Calculator", href: "/#parlay-calculator" },
  homepage: { label: "Explore Parlay Assistant", href: "/" },
  // Reserved for future tools — currently fall through to homepage.
  "round-robin-calculator": {
    label: "Try the Round Robin Calculator",
    href: "/round-robin-calculator",
  },
  "bankroll-tool": { label: "Explore Parlay Assistant", href: "/" },
  "hedge-calculator": { label: "Explore Parlay Assistant", href: "/" },
};

export const CATEGORIES = [
  { id: "basics", title: "Betting Basics", desc: "Core wager types every bettor should know." },
  { id: "money", title: "Managing Your Money", desc: "Simple rules for protecting a bankroll." },
  { id: "promos", title: "Sportsbook Promotions", desc: "How common offers actually work." },
];

// ---------------------------------------------------------------------------
// Lessons
// ---------------------------------------------------------------------------

export const LESSONS = [
  {
    id: 1,
    slug: "straight-bet",
    category: "basics",
    icon: "Target",
    title: "What Is a Straight Bet?",
    summary: "A straight bet is one wager on one result.",
    seoTitle: "What Is a Straight Bet? Simple Beginner Guide | Parlay Assistant",
    seoDescription:
      "Learn what a straight bet is, how it works, and how its payout is calculated using a simple beginner-friendly example.",
    readingTime: "3 min",
    tool: "odds-converter",
    body: {
      explain: [
        "A straight bet is the simplest kind of sports bet.",
        "You choose one thing that you believe will happen. Your entire bet depends on that one choice.",
        "For example, you might choose:",
      ],
      explainList: [
        "One team to win",
        "One team to cover the point spread",
        "A game to finish over or under a certain score",
        "One player to reach a certain number",
      ],
      explainAfter: [
        "If your one choice wins, your bet wins. If it loses, your bet loses.",
        "Unlike a parlay, you do not have to win several choices at the same time.",
      ],
      example: [
        "You bet $10 on Detroit at +150 to win.",
        "If Detroit wins:",
      ],
      exampleList: [
        "Your profit is $15",
        "Your original $10 is returned",
        "Your total payout is $25",
      ],
      exampleAfter: ["If Detroit loses, you lose the $10 wager."],
      whyItMatters: [
        "Straight bets are usually easier to understand and track than parlays. You only need one result to be correct.",
        "They can also make it easier to see whether your betting decisions are working over time.",
      ],
      mistake: [
        "A common mistake is thinking a straight bet is safe because it only has one choice.",
        "It is simpler, but it can still lose.",
      ],
      tip: "Start by understanding one wager before combining several wagers together.",
      rules: [
        "Ties",
        "Postponed games",
        "Players who do not participate",
        "Overtime",
        "Voided selections",
      ],
      rulesIntro: "Sportsbooks may use different rules for:",
      rulesOutro: "Always read the sportsbook's rules before betting.",
    },
    quiz: [
      {
        q: "How many selections are in a straight bet?",
        options: ["One", "Three", "Five"],
        correct: 0,
        explanation: "A straight bet is based on one selection.",
      },
      {
        q: "What must happen for a straight bet to win?",
        options: [
          "Every game that day must win",
          "Your one selection must win",
          "You must place another bet",
        ],
        correct: 1,
        explanation: "The result you selected must win under the sportsbook's rules.",
      },
      {
        q: "Is a straight bet guaranteed to win?",
        options: ["Yes", "No"],
        correct: 1,
        explanation: "Every sports bet carries risk.",
      },
    ],
  },
  {
    id: 2,
    slug: "parlay",
    category: "basics",
    icon: "Layers",
    title: "What Is a Parlay?",
    summary: "A parlay combines two or more selections into one wager.",
    seoTitle: "What Is a Parlay? Easy Explanation and Example | Parlay Assistant",
    seoDescription:
      "Learn how a parlay combines multiple bets, why parlays pay more, and why every leg must usually win.",
    readingTime: "4 min",
    tool: "parlay-calculator",
    body: {
      explain: [
        "A parlay is like putting several puzzle pieces into one bet.",
        "Each selection in a parlay is called a leg.",
        "For the parlay to win, every leg usually has to win.",
        "A three-leg parlay could include:",
      ],
      explainList: [
        "Detroit to win",
        "The basketball game to go over 220 points",
        "A player to score at least 20 points",
      ],
      explainAfter: [
        "If all three win, the parlay wins.",
        "If even one leg loses, the entire parlay usually loses.",
        "Parlays can pay more than straight bets because they are harder to win.",
      ],
      example: ["You place a $10 three-leg parlay."],
      exampleList: ["Leg 1 wins", "Leg 2 wins", "Leg 3 loses"],
      exampleAfter: [
        "The entire parlay loses because one leg was incorrect.",
        "If all three legs had won, the payout would have been larger than placing only one $10 straight bet.",
      ],
      whyItMatters: [
        "Parlays look exciting because a small wager can sometimes produce a large potential payout.",
        "However, adding more legs lowers the chance that every selection will win.",
      ],
      mistake: [
        "The biggest mistake is adding too many legs just because the potential payout looks large.",
        "A 10-leg parlay may show a huge payout, but it is much harder to win than a two-leg parlay.",
      ],
      tip: "Ask yourself whether every leg truly belongs in the bet. Do not add a weak selection only to make the payout bigger.",
      rulesIntro: "Check whether your sportsbook allows:",
      rules: [
        "Same-game parlays",
        "Correlated selections",
        "Voided legs",
        "Promotional boosts",
        "Player props when a player does not start",
      ],
    },
    quiz: [
      {
        q: "What is one selection in a parlay called?",
        options: ["A leg", "A unit", "A refund"],
        correct: 0,
        explanation: "Each selection is called a leg.",
      },
      {
        q: "What usually happens if one leg loses?",
        options: [
          "The entire parlay loses",
          "The parlay automatically doubles",
          "Nothing changes",
        ],
        correct: 0,
        explanation: "Most parlays require every leg to win.",
      },
      {
        q: "Why can parlays pay more?",
        options: [
          "They are harder to win",
          "They never lose",
          "Sportsbooks give away free money",
        ],
        correct: 0,
        explanation: "Combining several outcomes creates more risk.",
      },
    ],
  },
  {
    id: 3,
    slug: "round-robin",
    category: "basics",
    icon: "Repeat",
    title: "What Is a Round Robin Bet?",
    summary: "A round robin divides several selections into a group of smaller parlays.",
    seoTitle: "What Is a Round Robin Bet? Simple Explanation | Parlay Assistant",
    seoDescription:
      "Learn how a round robin turns several selections into smaller parlays and why the total wager can be larger than expected.",
    readingTime: "4 min",
    tool: "round-robin-calculator",
    body: {
      explain: [
        "A round robin takes your selections and creates several smaller parlay combinations.",
        "Suppose you select three teams:",
      ],
      explainList: ["Team A", "Team B", "Team C"],
      explainAfter: [
        "A round robin by twos creates these bets:",
        "• Team A and Team B",
        "• Team A and Team C",
        "• Team B and Team C",
        "That means you are not placing one parlay. You are placing three separate two-leg parlays.",
        "If one team loses, some of the smaller parlays may still win.",
      ],
      example: [
        "You choose three teams and place a round robin by twos.",
        "Each smaller parlay costs $5.",
        "There are three combinations, so your total wager is:",
        "3 bets × $5 = $15",
        "If Team A and Team B win but Team C loses:",
      ],
      exampleList: ["A + B wins", "A + C loses", "B + C loses"],
      exampleAfter: [
        "You win one of the three parlays.",
        "Whether you earn an overall profit depends on the odds and total money wagered.",
      ],
      whyItMatters: [
        "A round robin gives you more ways to win than one large parlay, but it costs more because you are placing several bets.",
      ],
      mistake: [
        "People sometimes look only at the amount shown for each combination and forget to check the total wager.",
        "A $5 round robin may cost much more than $5.",
      ],
      tip: "Always check three things before submitting: number of combinations, amount per combination, and total amount being wagered.",
      rulesIntro: "Sportsbooks may handle these differently:",
      rules: [
        "Ties",
        "Voided selections",
        "Same-game selections",
        "Maximum combinations",
        "Promotional credits",
      ],
    },
    quiz: [
      {
        q: "Is a round robin one single bet?",
        options: ["Yes", "No, it contains several smaller bets"],
        correct: 1,
        explanation: "A round robin creates multiple parlay combinations.",
      },
      {
        q: "If you wager $5 on three combinations, what is your total wager?",
        options: ["$5", "$10", "$15"],
        correct: 2,
        explanation: "Three combinations at $5 each cost $15.",
      },
      {
        q: "Can one smaller parlay win even if another one loses?",
        options: ["Yes", "No"],
        correct: 0,
        explanation: "Each combination is graded separately.",
      },
    ],
  },
  {
    id: 4,
    slug: "bankroll-management",
    category: "money",
    icon: "Wallet",
    title: "What Is Bankroll Management?",
    summary:
      "Bankroll management means deciding how much money you can afford to use and protecting it with clear limits.",
    seoTitle: "Bankroll Management Explained Simply | Parlay Assistant",
    seoDescription:
      "Learn how to create a betting bankroll, control wager sizes, and avoid risking money needed for everyday expenses.",
    readingTime: "4 min",
    tool: "bankroll-tool",
    body: {
      explain: [
        "Your bankroll is the money you have set aside only for sports betting.",
        "It should not include money needed for:",
      ],
      explainList: [
        "Rent",
        "Food",
        "Utilities",
        "Medicine",
        "Transportation",
        "Bills",
        "Savings goals",
      ],
      explainAfter: [
        "Think of your bankroll like a jar. Every bet takes money from the jar. Winning may put money back into it, but losing removes money.",
        "Bankroll management means making rules so one bad day does not empty the jar.",
      ],
      example: [
        "Suppose your betting bankroll is $100.",
        "Instead of betting $50 on one game, you decide that your normal bet will be $2.",
        "That gives you room to survive several losses without losing the entire bankroll.",
      ],
      whyItMatters: [
        "Even good decisions can lose. Sports are unpredictable.",
        "Managing your bankroll helps prevent one bet, one game, or one emotional decision from causing a large loss.",
      ],
      mistake: [
        "A major mistake is adding more money immediately after a loss because you want to win it back quickly.",
        "That is called chasing losses.",
      ],
      tip: "Choose your normal wager amount before games begin. Do not increase it because you are angry, excited, or trying to recover a loss.",
      rulesIntro: "Set personal limits for:",
      rules: ["Deposits", "Wagers", "Losses", "Time spent betting"],
      rulesOutro: "Use your sportsbook's responsible-gambling controls when needed.",
    },
    quiz: [
      {
        q: "Should rent money be part of a betting bankroll?",
        options: ["Yes", "No"],
        correct: 1,
        explanation: "Only use money you can afford to lose.",
      },
      {
        q: "Why use smaller wager amounts?",
        options: [
          "To help protect the bankroll",
          "To guarantee every bet wins",
          "To remove all risk",
        ],
        correct: 0,
        explanation: "Smaller wagers reduce the damage from a losing streak.",
      },
      {
        q: "What is chasing losses?",
        options: [
          "Increasing bets to quickly recover money",
          "Checking game scores",
          "Using an odds converter",
        ],
        correct: 0,
        explanation: "Chasing often leads to larger risks and losses.",
      },
    ],
  },
  {
    id: 5,
    slug: "betting-units",
    category: "money",
    icon: "Ruler",
    title: "What Is a Betting Unit?",
    summary: "A unit is a standard amount you use to measure your wagers.",
    seoTitle: "What Is a Betting Unit? Beginner Explanation | Parlay Assistant",
    seoDescription:
      "Learn how betting units create a consistent wager size and make results easier to track.",
    readingTime: "3 min",
    tool: "bankroll-tool",
    body: {
      explain: [
        "A unit is like using the same measuring cup every time you cook.",
        'Instead of saying, "I bet a lot" or "I bet a little," you choose one normal amount and call it one unit.',
        "For one person: 1 unit may be $1.",
        "For another person: 1 unit may be $10.",
        "The dollar amount should be based on that person's own bankroll, not someone else's.",
      ],
      example: [
        "Your bankroll is $200.",
        "You decide that one unit equals $4.",
        "That means:",
      ],
      exampleList: ["0.5 unit = $2", "1 unit = $4", "2 units = $8"],
      exampleAfter: [
        "If you win $12, you gained three units.",
        "If you lose $8, you lost two units.",
      ],
      whyItMatters: [
        "Units make it easier to compare results without focusing only on dollar amounts.",
        "They also help you avoid changing your wager size without a clear reason.",
      ],
      mistake: [
        "A common mistake is copying another bettor's dollar amount.",
        "Someone with a $5,000 bankroll can handle a different wager size than someone with a $100 bankroll.",
      ],
      tip: "Keep your normal unit small. A common conservative approach is approximately 1% to 2% of a bankroll, but no percentage removes risk.",
      rulesIntro: "Remember:",
      rules: [
        "Your unit size is a personal budgeting choice, not a prediction of how likely a bet is to win.",
      ],
    },
    quiz: [
      {
        q: "Is one unit the same dollar amount for everyone?",
        options: ["Yes", "No"],
        correct: 1,
        explanation: "A unit should be based on a person's own bankroll.",
      },
      {
        q: "If one unit is $5, how much is two units?",
        options: ["$5", "$7", "$10"],
        correct: 2,
        explanation: "Two units equal $10.",
      },
      {
        q: "Do units guarantee profits?",
        options: ["Yes", "No"],
        correct: 1,
        explanation: "Units help manage wager size, not guarantee results.",
      },
    ],
  },
  {
    id: 6,
    slug: "profit-boosts",
    category: "promos",
    icon: "TrendingUp",
    title: "What Is a Profit Boost?",
    summary: "A profit boost increases the profit portion of an eligible winning bet.",
    seoTitle: "How Sportsbook Profit Boosts Work | Parlay Assistant",
    seoDescription:
      "Learn how profit boosts increase potential winnings, what they do not increase, and which rules to check.",
    readingTime: "3 min",
    tool: "odds-converter",
    body: {
      explain: [
        "A profit boost is a sportsbook promotion.",
        "It may increase how much profit you receive if your bet wins.",
        "It normally does not increase your original wager. It increases only the profit.",
        "The boost may apply only to certain:",
      ],
      explainList: ["Sports", "Games", "Odds", "Bet types", "Wager amounts"],
      example: [
        "You place a $10 bet that would normally earn $10 in profit.",
        "A 25% profit boost adds 25% to that $10 profit.",
      ],
      exampleList: [
        "Normal profit: $10",
        "Extra boosted profit: $2.50",
        "Total profit: $12.50",
        "Total payout, including your wager: $22.50",
      ],
      exampleAfter: [
        "This example is simplified. The sportsbook's calculation and limits control the actual result.",
      ],
      whyItMatters: [
        "A boost may improve the value of a bet you already planned to make.",
        "It does not make a bad bet automatically good, and the bet can still lose.",
      ],
      mistake: [
        "The biggest mistake is placing a bet only because a boost is available.",
        "A promotion should not pressure you into making a wager you did not want.",
      ],
      tip: 'Compare the boosted odds with odds at other sportsbooks. A "boosted" price may not always be the best available price.',
      rulesIntro: "Check:",
      rules: [
        "Minimum odds",
        "Maximum wager",
        "Maximum extra winnings",
        "Eligible markets",
        "Expiration time",
        "Whether cash-out is allowed",
      ],
    },
    quiz: [
      {
        q: "Does a profit boost guarantee a win?",
        options: ["Yes", "No"],
        correct: 1,
        explanation: "It changes the potential profit, not the game result.",
      },
      {
        q: "What does a profit boost usually increase?",
        options: [
          "The profit on a winning eligible bet",
          "The score of the game",
          "Your original wager",
        ],
        correct: 0,
        explanation: "Boosts add to the profit portion of an eligible winning bet.",
      },
      {
        q: "Should you check the maximum boosted wager?",
        options: ["Yes", "No"],
        correct: 0,
        explanation: "Many boosts have limits.",
      },
    ],
  },
  {
    id: 7,
    slug: "no-sweat-bets",
    category: "promos",
    icon: "ShieldOff",
    title: "What Is a No-Sweat Bet or Bet Reset?",
    summary:
      "These promotions may return something after an eligible losing wager, but the return is often not withdrawable cash.",
    seoTitle: "No-Sweat Bets and Bet Resets Explained | Parlay Assistant",
    seoDescription:
      "Learn how no-sweat promotions work, what may be returned after a loss, and why they are not truly risk-free.",
    readingTime: "3 min",
    tool: "homepage",
    body: {
      explain: [
        "A no-sweat bet is a promotion that may give you something back if your eligible bet loses.",
        "The return may be:",
      ],
      explainList: [
        "A bonus bet",
        "Site credit",
        "A bet token",
        "Another promotional wager",
      ],
      explainAfter: [
        "It is often not the same as getting your original cash back.",
        'A "bet reset" may work in a similar way, but every sportsbook can use the name differently.',
      ],
      example: [
        "You place an eligible $20 wager.",
        "The wager loses.",
        "The sportsbook gives you a $20 bonus bet.",
        "You can use the bonus bet on another wager, but you may not be able to withdraw that $20 directly.",
        "If the bonus bet wins, the original bonus stake is commonly not included in the payout. Only the winnings may be returned.",
      ],
      whyItMatters: [
        'The words "no sweat" can make a promotion sound safer than it really is.',
        "You may still lose your original cash, and the replacement credit may have restrictions.",
      ],
      mistake: [
        'A common mistake is believing "no sweat" means "no risk."',
        "It does not.",
      ],
      tip: "Before opting in, read exactly what you receive if the wager loses.",
      rulesIntro: "Check:",
      rules: [
        "Maximum qualifying wager",
        "Minimum odds",
        "Eligible bet types",
        "Whether the return is cash or bonus credit",
        "Expiration date",
        "Whether the bonus stake is returned",
        "Opt-in requirements",
      ],
    },
    quiz: [
      {
        q: "Is a no-sweat return always cash?",
        options: ["Yes", "No"],
        correct: 1,
        explanation: "It is often a bonus bet or promotional credit.",
      },
      {
        q: "Can the original wager still lose?",
        options: ["Yes", "No"],
        correct: 0,
        explanation: 'No-sweat does not mean risk-free.',
      },
      {
        q: "Should you read the promotion rules before betting?",
        options: ["Yes", "No"],
        correct: 0,
        explanation: "Rules define what actually happens after a loss.",
      },
    ],
  },
  {
    id: 8,
    slug: "futures-bets",
    category: "basics",
    icon: "Clock",
    title: "What Is a Futures Bet?",
    summary: "A futures bet is a wager on something that will be decided later.",
    seoTitle: "What Is a Futures Bet? Simple Beginner Guide | Parlay Assistant",
    seoDescription:
      "Learn how futures bets predict an outcome that will be decided later in a season or tournament.",
    readingTime: "3 min",
    tool: "odds-converter",
    body: {
      explain: [
        "A futures bet is like making a prediction now and waiting a long time to see whether it is correct.",
        "You might bet on:",
      ],
      explainList: [
        "A team to win a championship",
        "A player to win an award",
        "A team to win its division",
        "A player to lead the league in a statistic",
      ],
      explainAfter: ["The wager may stay open for weeks or months."],
      example: [
        "Before the football season begins, you bet $10 on Detroit to win the championship at +1000.",
        "If Detroit wins the championship:",
      ],
      exampleList: [
        "Profit: $100",
        "Original wager returned: $10",
        "Total payout: $110",
      ],
      exampleAfter: ["If Detroit does not win, the wager loses."],
      whyItMatters: [
        "Futures can offer large potential payouts, but your money may remain tied up for a long time.",
        "Odds can also change during the season.",
      ],
      mistake: [
        "A common mistake is looking only at the large payout and ignoring how difficult the outcome is.",
      ],
      tip: "Do not risk money you may need before the season or tournament ends.",
      rulesIntro: "Check how the sportsbook handles:",
      rules: [
        "Injured players",
        "Traded players",
        "Shortened seasons",
        "Team relocation",
        "Ties",
        "Award voting",
        "Cash-out offers",
      ],
    },
    quiz: [
      {
        q: "When is a futures bet usually decided?",
        options: ["Immediately", "Later in the season or event", "Before you place it"],
        correct: 1,
        explanation: "Futures resolve at a future date.",
      },
      {
        q: "Can your money remain tied up for months?",
        options: ["Yes", "No"],
        correct: 0,
        explanation: "Futures may stay open for weeks or months.",
      },
      {
        q: "Does a large payout mean the bet is likely to win?",
        options: ["Yes", "No"],
        correct: 1,
        explanation: "Large payouts often reflect a low estimated chance.",
      },
    ],
  },
  {
    id: 9,
    slug: "hedging-bets",
    category: "money",
    icon: "Scale",
    title: "What Does It Mean to Hedge a Bet?",
    summary:
      "Hedging means placing another wager that can reduce your risk from an earlier wager.",
    seoTitle: "What Does Hedging a Bet Mean? Easy Explanation | Parlay Assistant",
    seoDescription:
      "Learn how hedging uses another wager to reduce risk or protect part of a possible payout.",
    readingTime: "4 min",
    tool: "hedge-calculator",
    body: {
      explain: [
        "Imagine you have a bet that can win a large amount of money, but one final result still needs to happen.",
        "You may place a second wager on the other side.",
        "That second wager is called a hedge.",
        "A hedge may help you:",
      ],
      explainList: [
        "Protect part of a possible profit",
        "Reduce a possible loss",
        "Create a more predictable result",
      ],
      explainAfter: ["A hedge does not always produce equal profit on both sides."],
      example: [
        "You have a futures ticket that will pay $500 if Team A wins the championship.",
        "Team A reaches the final and plays Team B.",
        "You place a second bet on Team B.",
        "Now:",
      ],
      exampleList: [
        "If Team A wins, your original ticket wins",
        "If Team B wins, your second wager may win",
      ],
      exampleAfter: [
        "The amount wagered on Team B determines how much you make or lose in each outcome.",
      ],
      whyItMatters: [
        "Hedging can reduce risk, but it can also reduce your largest possible profit.",
      ],
      mistake: [
        "A common mistake is guessing the hedge amount without calculating both outcomes.",
      ],
      tip: "Write down your profit or loss for every possible result before placing the hedge.",
      rulesIntro: "Check:",
      rules: [
        "Current odds",
        "Wager limits",
        "Cash-out availability",
        "Whether a tie or third outcome is possible",
        "Whether both bets can be voided differently",
      ],
    },
    quiz: [
      {
        q: "What is the main purpose of a hedge?",
        options: [
          "Reduce or manage risk",
          "Guarantee a team scores",
          "Cancel all sportsbook rules",
        ],
        correct: 0,
        explanation: "Hedges are risk-management tools.",
      },
      {
        q: "Can hedging reduce your largest possible profit?",
        options: ["Yes", "No"],
        correct: 0,
        explanation: "Reducing risk usually reduces upside.",
      },
      {
        q: "Should you calculate every outcome first?",
        options: ["Yes", "No"],
        correct: 0,
        explanation: "Every outcome should be understood before committing money.",
      },
    ],
  },
  {
    id: 10,
    slug: "deposit-matches",
    category: "promos",
    icon: "PiggyBank",
    title: "What Is a Deposit Match?",
    summary:
      "A deposit match is a promotion that may add bonus value after you deposit qualifying money.",
    seoTitle: "How Sportsbook Deposit Matches Work | Parlay Assistant",
    seoDescription:
      "Learn how deposit-match promotions work and why wagering requirements, limits, and expiration dates matter.",
    readingTime: "3 min",
    tool: "homepage",
    body: {
      explain: [
        "A sportsbook may offer to match part of your deposit.",
        "For example, a 50% deposit match means the sportsbook may add 50 cents in promotional value for every qualifying dollar you deposit, up to a limit.",
        "It usually does not mean free cash that you can withdraw right away.",
        "The bonus may have rules about how many times it must be wagered.",
      ],
      example: [
        "A sportsbook offers a 50% deposit match up to $100.",
        "You deposit $100.",
        "A 50% match may provide $50 in promotional value.",
        "However, you may need to wager the deposit or bonus before any related money can be withdrawn.",
      ],
      whyItMatters: [
        "The size of the advertised bonus does not tell the whole story.",
        "The rules decide how useful the promotion really is.",
      ],
      mistake: [
        "A common mistake is depositing more money than planned only to receive a larger match.",
      ],
      tip: "Decide your deposit limit first. Do not let the promotion choose your budget.",
      rulesIntro: "Check:",
      rules: [
        "Minimum deposit",
        "Maximum match",
        "Wagering requirement",
        "Minimum odds",
        "Eligible games and markets",
        "Expiration date",
        "Withdrawal restrictions",
      ],
    },
    quiz: [
      {
        q: "Is a deposit match always immediately withdrawable cash?",
        options: ["Yes", "No"],
        correct: 1,
        explanation: "Bonus value usually has wagering requirements.",
      },
      {
        q: "What is a wagering requirement?",
        options: [
          "A rule saying how much must be bet before withdrawal",
          "A guaranteed win",
          "A game score",
        ],
        correct: 0,
        explanation: "Wagering requirements gate withdrawals.",
      },
      {
        q: "Should you deposit more than you can afford because of a promotion?",
        options: ["Yes", "No"],
        correct: 1,
        explanation: "Set your budget first — do not let promotions choose it.",
      },
    ],
  },
  {
    id: 11,
    slug: "bonus-bets",
    category: "promos",
    icon: "Gift",
    title: "What Is a Bonus Bet?",
    summary: "A bonus bet is promotional wagering credit, not usually withdrawable cash.",
    seoTitle: "How Bonus Bets Work: Simple Explanation | Parlay Assistant",
    seoDescription:
      "Learn how sportsbook bonus bets differ from cash and how payouts are commonly calculated.",
    readingTime: "3 min",
    tool: "odds-converter",
    body: {
      explain: [
        "A bonus bet is a token the sportsbook lets you use to place a wager.",
        "You normally cannot withdraw the bonus amount itself.",
        "If the bet wins, you commonly receive the profit, but not the bonus stake.",
      ],
      example: [
        "You use a $10 bonus bet at +200.",
        "The wager wins.",
      ],
      exampleList: [
        "Profit: $20",
        "Bonus stake returned: commonly $0",
        "Total amount credited: commonly $20",
      ],
      exampleAfter: [
        "If you had used $10 in cash, the total payout would normally include both the $20 profit and your $10 wager.",
        "Rules vary, so always check the promotion.",
      ],
      whyItMatters: [
        "A $10 bonus bet is not always worth the same as $10 cash.",
        "The odds you choose can affect how much value you receive.",
      ],
      mistake: [
        "A common mistake is expecting the bonus stake to be returned with the winnings.",
      ],
      tip: 'Check whether the promotion says "stake not returned."',
      rulesIntro: "Check:",
      rules: [
        "Expiration date",
        "Minimum odds",
        "Eligible markets",
        "Whether the stake is returned",
        "Whether the bonus can be divided",
        "Maximum winnings",
        "Withdrawal requirements",
      ],
    },
    quiz: [
      {
        q: "Can you usually withdraw an unused bonus bet as cash?",
        options: ["Yes", "No"],
        correct: 1,
        explanation: "Bonus bets are not usually withdrawable.",
      },
      {
        q: "Is the bonus stake commonly returned after a win?",
        options: ["Always", "Commonly not"],
        correct: 1,
        explanation: "Only the profit is commonly returned.",
      },
      {
        q: "Should you check the expiration date?",
        options: ["Yes", "No"],
        correct: 0,
        explanation: "Bonus bets expire — check the deadline before planning.",
      },
    ],
  },
  {
    id: 12,
    slug: "live-vs-pregame-betting",
    category: "basics",
    icon: "Radio",
    title: "Live Betting vs. Pregame Betting",
    summary:
      "Pregame bets are placed before an event begins, while live bets are placed after it has started.",
    seoTitle: "Live Betting vs. Pregame Betting Explained | Parlay Assistant",
    seoDescription:
      "Learn the difference between placing a wager before a game and placing one while the game is being played.",
    readingTime: "3 min",
    tool: "odds-converter",
    body: {
      explain: [
        "A pregame bet is placed before the game begins.",
        "A live bet is placed while the game is happening.",
        "Live odds can change quickly because the sportsbook reacts to:",
      ],
      explainList: [
        "The score",
        "Time remaining",
        "Injuries",
        "Penalties",
        "Possession",
        "Player performance",
      ],
      explainAfter: [
        "A team that was favored before the game may become an underdog during the game.",
      ],
      example: [
        "Before a basketball game, Team A is -150 to win.",
        "Team A falls behind by 15 points.",
        "During the game, its live odds may change to +200.",
        "The new odds offer a larger payout because Team A now appears less likely to win.",
      ],
      whyItMatters: [
        "Live betting gives you more information because you can see part of the game.",
        "However, it also gives you less time to think, and odds can change before you submit the wager.",
      ],
      mistake: [
        "A common mistake is making a fast live bet because of excitement, anger, or fear of missing out.",
      ],
      tip: "Set rules before the game. Decide which situations would interest you and how much you are willing to risk.",
      rulesIntro: "Check:",
      rules: [
        "Whether the displayed odds changed before acceptance",
        "Live-bet delays",
        "Maximum wagers",
        "Suspended markets",
        "Overtime rules",
        "Cash-out restrictions",
      ],
    },
    quiz: [
      {
        q: "When is a pregame bet placed?",
        options: ["Before the event begins", "After the event ends", "Only at halftime"],
        correct: 0,
        explanation: "Pregame bets are placed before kickoff or tipoff.",
      },
      {
        q: "Why do live odds change?",
        options: [
          "The game situation changes",
          "They never change",
          "The visitor chooses the odds",
        ],
        correct: 0,
        explanation: "Live odds react to the game in progress.",
      },
      {
        q: "Is live betting always safer because you watched part of the game?",
        options: ["Yes", "No"],
        correct: 1,
        explanation: "The outcome remains uncertain.",
      },
    ],
  },
  {
    id: 13,
    slug: "cash-out-options",
    category: "money",
    icon: "LogOut",
    title: "What Is a Cash-Out Option?",
    summary: "A cash-out offer lets you settle an open wager before the event is finished.",
    seoTitle: "Sportsbook Cash-Out Options Explained | Parlay Assistant",
    seoDescription:
      "Learn how cash-out offers work and why the amount may be lower than the full possible payout.",
    readingTime: "3 min",
    tool: "homepage",
    body: {
      explain: [
        "A cash-out button may let you end your bet early.",
        "The sportsbook offers you an amount based on what is happening at that moment.",
        "The offer may be:",
      ],
      explainList: [
        "More than your original wager",
        "Less than your original wager",
        "Much less than the full possible payout",
      ],
      explainAfter: ["The offer can change quickly or disappear."],
      example: [
        "You place a $10 parlay that could pay $100.",
        "Most legs have won, but one game is still being played.",
        "The sportsbook offers you $45 to cash out.",
        "You can:",
      ],
      exampleList: [
        "Take $45 now and end the bet",
        "Let the parlay continue for a chance at $100",
      ],
      exampleAfter: ["If you continue, the final leg could win or lose."],
      whyItMatters: [
        "Cash-out offers let you change your risk, but sportsbooks calculate the offer for their own business purposes.",
        "The offer may not be the fairest mathematical value.",
      ],
      mistake: [
        "A common mistake is pressing cash out because of panic without checking the remaining odds or possible outcomes.",
      ],
      tip: "Before accepting, compare: cash-out amount, full possible payout, current chance of winning, and whether another hedge option exists.",
      rulesIntro: "Cash out may be unavailable because of:",
      rules: [
        "Suspended markets",
        "Odds changes",
        "Connection delays",
        "Promotional restrictions",
        "Certain bet types",
      ],
    },
    quiz: [
      {
        q: "Does cashing out end the original wager?",
        options: ["Yes", "No"],
        correct: 0,
        explanation: "Cash-out settles the wager immediately.",
      },
      {
        q: "Is the cash-out amount always the full potential payout?",
        options: ["Yes", "No"],
        correct: 1,
        explanation: "It usually is not.",
      },
      {
        q: "Can a cash-out offer disappear?",
        options: ["Yes", "No"],
        correct: 0,
        explanation: "Offers change with the game and can be withdrawn.",
      },
    ],
  },
  {
    id: 14,
    slug: "bet-protection",
    category: "promos",
    icon: "Umbrella",
    title: "What Is Bet Protection?",
    summary:
      "Bet protection is a promotion that may provide a refund or credit when a specific covered event occurs.",
    seoTitle: "What Is Sportsbook Bet Protection? | Parlay Assistant",
    seoDescription:
      "Learn what bet protection may cover, what it may return, and why every promotion has different rules.",
    readingTime: "3 min",
    tool: "homepage",
    body: {
      explain: [
        "Bet protection is like a small umbrella that works only in certain kinds of rain.",
        "A sportsbook may protect a wager when something specific happens, such as:",
      ],
      explainList: [
        "One parlay leg losing",
        "A player leaving early because of injury",
        "A team losing after meeting a promotion rule",
        "An eligible wager losing",
      ],
      explainAfter: [
        "The protection does not cover every loss.",
        "The refund may be a bonus bet instead of cash.",
      ],
      example: [
        "A sportsbook offers parlay protection for an eligible five-leg parlay.",
        "Four legs win and one leg loses.",
        "The promotion may return the wager as a bonus bet, up to a stated limit.",
        "If two legs lose, the promotion may not apply.",
        "The exact result depends on the rules.",
      ],
      whyItMatters: [
        'The word "protection" can sound like a guarantee, but it is only a promotion with specific conditions.',
      ],
      mistake: [
        "A common mistake is assuming every wager or every kind of loss is covered.",
      ],
      tip: "Read the protection rules before placing the bet — not after it loses.",
      rulesIntro: "Check:",
      rules: [
        "Eligible bet types",
        "Required number of legs",
        "Minimum odds per leg",
        "Maximum protected amount",
        "Refund type",
        "Expiration date",
        "Injury requirements",
        "Opt-in requirements",
        "Excluded markets",
      ],
    },
    quiz: [
      {
        q: "Does bet protection cover every losing bet?",
        options: ["Yes", "No"],
        correct: 1,
        explanation: "Coverage depends on the specific promotion rules.",
      },
      {
        q: "Can the return be a bonus bet instead of cash?",
        options: ["Yes", "No"],
        correct: 0,
        explanation: "Refunds are often issued as bonus bets.",
      },
      {
        q: "When should you read the rules?",
        options: [
          "Before placing the wager",
          "Only after the wager loses",
        ],
        correct: 0,
        explanation: "Reading rules first prevents surprises.",
      },
    ],
  },
];

// Lookup helpers -----------------------------------------------------------

export const lessonBySlug = (slug) => LESSONS.find((l) => l.slug === slug);
export const lessonById = (id) => LESSONS.find((l) => l.id === id);
export const lessonsByCategory = (cat) => LESSONS.filter((l) => l.category === cat);

export const neighborLessons = (currentSlug) => {
  const idx = LESSONS.findIndex((l) => l.slug === currentSlug);
  if (idx === -1) return { prev: null, next: null };
  return {
    prev: idx > 0 ? LESSONS[idx - 1] : null,
    next: idx < LESSONS.length - 1 ? LESSONS[idx + 1] : null,
  };
};

// Related lessons: prefer same category; back-fill with cross-category
// lessons so every page has three contextual internal links for
// crawlability and SEO topic clustering.
export const relatedLessons = (currentSlug) => {
  const current = lessonBySlug(currentSlug);
  if (!current) return [];
  const same = LESSONS.filter(
    (l) => l.category === current.category && l.slug !== currentSlug,
  );
  if (same.length >= 3) return same.slice(0, 3);
  const others = LESSONS.filter(
    (l) => l.category !== current.category && l.slug !== currentSlug,
  );
  return [...same, ...others].slice(0, 3);
};

export const RESPONSIBLE_NOTICE =
  "Play Responsibly: Sports betting involves risk, and no wager is guaranteed to win. Only use money you can afford to lose. Do not use money needed for bills, food, housing, transportation, medicine, or savings. Promotions, odds, and sportsbook rules vary. Parlay Assistant provides educational information and calculation tools only. It does not accept wagers or operate as a sportsbook. You must meet the legal gambling age and follow the laws in your location.";
